// src/Pages/FollowUpPage.js
import { useEffect, useState } from "react";
import axios from "axios";
// MUI Components
import { Paper, Box, Grid, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, } from "@mui/material";
// React Icons
import { MdExpandMore } from "react-icons/md";
import { HiOutlineClock, HiOutlineCalendar, HiOutlinePlusCircle, HiOutlineXCircle } from "react-icons/hi";
import { FaHourglassHalf } from "react-icons/fa6";
import { FaCalendarAlt } from "react-icons/fa";
// MUI History icon
import HistoryIcon from "@mui/icons-material/History";
import DateInput from "../components/DateInput";
import FloatingLabelInput from "../components/FloatingLabelInput";
import Dropdown from "../components/Dropdown";
import EnquiryForm from "./InquiryForm";
import HistoryManagement from "./HistoryManagement";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

// MUI Icons
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import PhoneIcon from "@mui/icons-material/Phone";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// Helper to convert "YYYY-MM-DD" → "DD-MM-YYYY"
const formatYMDToDMY = (ymd) => {
  if (!ymd) return "";
  const [year, month, day] = ymd.split("-");
  return `${day}-${month}-${year}`;
};

// Base API path and category list for enquiries.
// const path = "http://103.251.218.254:3100";
const path = "http://192.168.1.248:3103";
const categories = ["Missed", "Today", "Next", "Delayed", "Failed"];

// Helper function to split records for a team leader.
const splitRecordsForTeamLeader = (records, userId) => {
  const selfRecords = records.filter(record => record.userId === userId);
  const teamRecords = records.filter(record => record.userId !== userId);
  return { self: selfRecords, team: teamRecords };
};

const FollowUpPage = ({ loggedInUser, companyData }) => {
  // -------------------- STATE DECLARATIONS --------------------
  // For regular users: dataSets[category] is an array.
  // For team leaders: dataSets[category] is an object { self: [], team: [] }.
  const [dataSets, setDataSets] = useState({});
  const [visibleTable, setVisibleTable] = useState(null);
  const [recordCounts, setRecordCounts] = useState({});
  const [offsets, setOffsets] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [inquiryNumber, setInquiryNumber] = useState("");

  // Pagination state.
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayedRecords, setDisplayedRecords] = useState(0);

  // Individual search field state variables.
  const [searchInquiryDate, setSearchInquiryDate] = useState("");
  const [searchInquiryNumber, setSearchInquiryNumber] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchAssign, setSearchAssign] = useState("");
  const [searchCompanyName, setSearchCompanyName] = useState("");
  const [searchContactPerson, setSearchContactPerson] = useState("");
  const [searchMobileNo, setSearchMobileNo] = useState("");

  // Dropdown options for filtering.
  const [statusOptions, setStatusOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  // Flag to indicate whether a search is active.
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Modal states.
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showEnquiryFormModal, setShowEnquiryFormModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyInquiryId, setHistoryInquiryId] = useState(null);

  // Responsive handling using theme and media query.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // -------------------- EFFECTS & RESPONSIVE HANDLING --------------------
  // Update window width state on resize.
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // -------------------- DATA FETCHING FUNCTIONS --------------------
  /**
   * fetchEnquiries
   * ----------------
   * Fetches enquiries from the backend based on the given category, offset, and filter values.
   * @param {string} category - The enquiry category (e.g., "Today").
   * @param {number} currentOffset - The offset for pagination.
   * @param {object|null} filtersOverride - Optional. If provided, these filters override the current search states.
   * @returns {Array} - Array of fetched enquiry records.
   */
  const fetchEnquiries = async (category, currentOffset = 0, filtersOverride = null) => {
    try {
      // Build filters object.
      let filtersToSend = {};
      if (filtersOverride === null) {
        if (searchInquiryDate.trim() !== "") {
          filtersToSend.inquiryDate = searchInquiryDate.trim();
        }
        if (searchInquiryNumber.trim() !== "") {
          filtersToSend.inquiryNumber = searchInquiryNumber.trim();
        }
        if (searchStatus.trim() !== "") {
          filtersToSend.status = searchStatus.trim();
        }
        if (searchAssign.trim() !== "") {
          filtersToSend.assign = searchAssign.trim();
        }
        if (searchCompanyName.trim() !== "") {
          filtersToSend.companyName = searchCompanyName.trim();
        }
        if (searchContactPerson.trim() !== "") {
          filtersToSend.contactPerson = searchContactPerson.trim();
        }
        if (searchMobileNo.trim() !== "") {
          filtersToSend.mobileNo = searchMobileNo.trim();
        }
      } else {
        // Use the provided filters (for resetting, this will be an empty object).
        filtersToSend = filtersOverride;
      }

      // Build API query parameters.
      const params = { category, offset: currentOffset, limit: 10, userId: loggedInUser.userId, companyId: loggedInUser.companyId, role: loggedInUser.userCategory, ...filtersToSend, };
      const res = await axios.get(`${path}/api/get-enquiries`, { params });
      console.log("Fetched enquiries:", res.data);
      if (res.data.total !== undefined) {
        setTotalRecords(res.data.total);
        if (currentOffset === 0) {
          setDisplayedRecords(res.data.enquiries.length);
        } else {
          setDisplayedRecords(prev => prev + res.data.enquiries.length);
        }
      }
      return res.data.enquiries || [];
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      return [];
    }
  };

  // -------------------- INITIAL DATA LOAD & COUNTS --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const newData = {};
        const newOffsets = {};
        for (const cat of categories) {
          const records = await fetchEnquiries(cat, 0);
          if (loggedInUser.userCategory === "Team Leader") {
            const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
            newData[cat] = splitData;
            newOffsets[cat] = { self: splitData.self.length, team: splitData.team.length };
          } else {
            newData[cat] = records;
            newOffsets[cat] = records.length;
          }
        }
        setDataSets(newData);
        setOffsets(newOffsets);

        const countRes = await axios.get(`${path}/api/get-counts`, { params: { userId: loggedInUser.userId, role: loggedInUser.userCategory, companyId: loggedInUser?.companyId }, });
        let counts = countRes.data;
        setRecordCounts(counts);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchData();
  }, [loggedInUser]);

  // -------------------- FETCH DROPDOWN OPTIONS WHEN SEARCH MODAL OPENS --------------------
  useEffect(() => {
    if (showSearchModal) {
      axios.get(`${path}/api/status`).then((res) => setStatusOptions(Array.isArray(res.data.data) ? res.data.data : [])).catch((error) => console.error("Error fetching status:", error));
      axios.get(`${path}/api/users`, { params: { companyId: loggedInUser.companyId } }).then((res) => setUsersOptions(Array.isArray(res.data.data) ? res.data.data : [])).catch((error) => console.error("Error fetching users:", error));
    }
  }, [showSearchModal]);

  // -------------------- FETCH THE LATEST INQUIRY NUMBER ON MOUNT --------------------
  useEffect(() => {
    const fetchInquiryNumber = async () => {
      try {
        const res = await axios.get(`${path}/api/getInqNumber`);
        setInquiryNumber(res.data.inquiryNumber);
      } catch (error) {
        console.error("Error fetching inquiry number:", error);
      }
    };
    fetchInquiryNumber();
  }, []);

  // -------------------- EVENT HANDLERS --------------------
  /**
   * toggleTable
   * -----------
   * Clears individual search states, deactivates search mode, and toggles the visible table.
   * Also closes the search modal.
   * @param {string} type - The selected enquiry category.
   */
  const toggleTable = (type) => {
    // Clear individual search filter states.
    setSearchInquiryDate("");
    setSearchInquiryNumber("");
    setSearchStatus("");
    setSearchAssign("");
    setSearchCompanyName("");
    setSearchContactPerson("");
    setSearchMobileNo("");
    setIsSearchActive(false);
    // Toggle the visible table.
    setVisibleTable((prev) => (prev === type ? null : type));
    // Close the search modal.
    setShowSearchModal(false);
  };

  // Individual search field handlers.
  const handleSearchInquiryDateChange = (e) => setSearchInquiryDate(e.target.value);
  const handleSearchInquiryNumberChange = (e) => setSearchInquiryNumber(e.target.value);
  const handleSearchCompanyNameChange = (e) => setSearchCompanyName(e.target.value);
  const handleSearchContactPersonChange = (e) => setSearchContactPerson(e.target.value);
  const handleSearchMobileNoChange = (e) => setSearchMobileNo(e.target.value);

  /**
   * handleModalSearch
   * -----------------
   * Triggered when the Search button in the search modal is clicked.
   * It fetches enquiries based on the current filter values and updates the displayed data.
   */
  const handleModalSearch = async () => {
    if (visibleTable) {
      const records = await fetchEnquiries(visibleTable, 0);
      if (loggedInUser.userCategory === "Team Leader") {
        const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
        setDataSets((prev) => ({ ...prev, [visibleTable]: splitData }));
      } else {
        setDataSets((prev) => ({ ...prev, [visibleTable]: records }));
      }
      setIsSearchActive(true);
      setShowSearchModal(false);
    }
  };

  /**
   * handleCloseSearchModal (Reset Search)
   * ---------------------------------------
   * Clears all search filters and reloads the enquiries for the visible category exactly as initially loaded.
   * Uses an override of filters (an empty object) to ensure no filters are applied.
   */
  // Helper: return color based on priority
  const getPriorityColor = (priority) => {
    if (!priority) return "inherit";
    const p = priority.toLowerCase();
    if (p === "high") return "red";
    if (p === "medium") return "orange";
    if (p === "low") return "green";
    return "inherit";
  };
  const handleCloseSearchModal = async () => {
    // Clear search filter states.
    setSearchInquiryDate("");
    setSearchInquiryNumber("");
    setSearchStatus("");
    setSearchAssign("");
    setSearchCompanyName("");
    setSearchContactPerson("");
    setSearchMobileNo("");
    setIsSearchActive(false);
    // Close the search modal.
    setShowSearchModal(false);
    // Reload enquiries without filters (offset 0).
    if (visibleTable) {
      const records = await fetchEnquiries(visibleTable, 0, {}); // Pass an empty filter override.
      if (loggedInUser.userCategory === "Team Leader") {
        const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
        setDataSets((prev) => ({ ...prev, [visibleTable]: splitData }));
        // Optionally update offsets if needed.
        setOffsets((prev) => ({ ...prev, [visibleTable]: { self: splitData.self.length, team: splitData.team.length } }));
      } else {
        setDataSets((prev) => ({ ...prev, [visibleTable]: records }));
        setOffsets((prev) => ({ ...prev, [visibleTable]: records.length }));
      }
    }
  };

  /**
   * handleRowClick
   * --------------
   * Triggered when a record row is clicked.
   * Sets the selected record and opens the enquiry form modal.
   * @param {object} record - The clicked enquiry record.
   */
  const handleRowClick = (record) => {
    console.log(record);
    setSelectedRecord(record);
    setShowEnquiryFormModal(true);
  };

  /**
   * handleOpenHistory
   * -----------------
   * Opens the history modal for the specified enquiry.
   * @param {number|string} inquiryId - The ID of the enquiry.
   */
  const handleOpenHistory = (inquiryId) => {
    console.log(inquiryId);
    setHistoryInquiryId(inquiryId);
    setShowHistoryModal(true);
  };

  // For team leaders, merge self and team enquiries into one array.
  let mergedInquiries = [];
  if (visibleTable && loggedInUser.userCategory === "Team Leader") {
    mergedInquiries = [...(dataSets[visibleTable]?.self || []), ...(dataSets[visibleTable]?.team || []),];
  }

  /**
   * handleShowMore
   * --------------
   * Fetches the next batch of records (pagination) for the currently visible category.
   */
  const handleShowMore = async () => {
    if (!visibleTable) return;
    try {
      const currentOffset = offsets[visibleTable] || 0;
      const newRecords = await fetchEnquiries(visibleTable, currentOffset);
      if (loggedInUser.userCategory === "Team Leader") {
        const splitNew = splitRecordsForTeamLeader(newRecords, loggedInUser.userId);
        setDataSets((prev) => ({ ...prev, [visibleTable]: { self: [...(prev[visibleTable]?.self || []), ...splitNew.self], team: [...(prev[visibleTable]?.team || []), ...splitNew.team], }, }));
      } else {
        if (newRecords.length > 0) {
          setDataSets((prev) => ({ ...prev, [visibleTable]: [...prev[visibleTable], ...newRecords] }));
        }
      }
      setDisplayedRecords(prev => prev + newRecords.length);
    } catch (error) {
      console.error("Error fetching more records:", error);
    }
  };

  const avatarColors = ["#004225", "#800020", "#006D77", "#FF6F00"];
  // -------------------- COMPONENT RENDER --------------------
  return (
    <Box sx={{ width: '100%', maxWidth: '1600px', mx: 'auto', px: { xs: 1, sm: 2, md: 3 }, mt: 1 }}>
      <Typography variant="h4" align="center" gutterBottom sx={{ textDecoration: "underline" }}> Inquiry Follow-Up </Typography>

      {/* MASTER CARDS SECTION */}
      <Box
        sx={{
          display: "grid",
          gap: { xs: 1, sm: 3, md: 3 },
          my: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
        }}
      >
        {categories.map((type, index) => {
          const colors = [
            "#facc15", // yellow
            "#4ade80", // green
            "#60a5fa", // blue
            "#a78bfa", // purple
            "#f87171", // red
          ];

          const icons = {
            Missed: <HiOutlineClock style={{ width: 26, height: 26, color: "white" }} />,
            Today: <HiOutlineCalendar style={{ width: 26, height: 26, color: "white" }} />,
            Next: <FaCalendarAlt style={{ width: 26, height: 26, color: "white" }} />,
            Delayed: <FaHourglassHalf style={{ width: 26, height: 26, color: "white" }} />,
            Failed: <HiOutlineXCircle style={{ width: 26, height: 26, color: "white" }} />,
          };

          const categoryKey = type === "Next" ? "Next" : type;

          return (
            <Box key={type} onClick={() => toggleTable(type)}
              sx={{
                cursor: "pointer",
                borderRadius: 4,
                boxShadow: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: { xs: "row", sm: "column" },
                textAlign: "center",
                background: `linear-gradient(135deg, ${colors[index % colors.length]}, ${colors[index % colors.length]}CC)`,
                color: "#fff",
                transition: "transform 0.25s ease-in-out, box-shadow 0.25s ease-in-out",
                width: "100%",
                height: { xs: 50, sm: 110, md: 130, lg: 140 },
                p: { xs: 1.5, sm: 3 },
                "&:hover": {
                  transform: "scale(1.04)",
                  boxShadow: 5,
                },
              }}
            >
              {/* Icon */}
              <Box sx={{ mb: { xs: 0, sm: 1 }, ml: { xs: 1, sm: 0 }, mr: { xs: 1, sm: 0 } }}>
                {icons[categoryKey]}
              </Box>

              {/* Label */}
              <Typography variant="subtitle2"
                sx={{
                  flex: 1,
                  fontSize: { xs: "0.95rem", sm: "0.9rem", md: "1rem" },
                  fontWeight: 600,
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                {type}
              </Typography>

              {/* Count */}
              <Typography variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                  textAlign: "right",
                  mr: { xs: 1, sm: 0 },
                }}
              >
                {recordCounts[type] ?? 0}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* TOP SECTION: Search, Count & Show More */}
      {visibleTable && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "space-between", mb: 2, px: { xs: 2, md: 1 }, mt: 2, }}>
          <Typography variant="subtitle1" sx={{ mb: { xs: 2, md: 0 } }}> Count:{" "} {loggedInUser.userCategory === "Team Leader" ? `${(dataSets[visibleTable]?.self || []).length + (dataSets[visibleTable]?.team || []).length}` : dataSets[visibleTable]?.length || 0}{" "} / {recordCounts[visibleTable] || 0} </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Open search modal to enter filter values */}
            <Button variant="contained" color="success" onClick={() => setShowSearchModal(true)} startIcon={<HiOutlinePlusCircle />} sx={{ height: 40, borderRadius: 1 }}> Search </Button>
            {/* Load more records if available */}
            <Button variant="contained" color="primary" onClick={handleShowMore} disabled={displayedRecords >= totalRecords} sx={{ borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}> Show More <MdExpandMore size={20} style={{ marginLeft: 4 }} /> </Box>
            </Button>
          </Box>
        </Box>
      )}

      {/* SEARCH POPUP MODAL */}
      <Dialog open={showSearchModal} onClose={handleCloseSearchModal} fullWidth maxWidth="sm" BackdropProps={{ onClick: handleCloseSearchModal }} >
        <DialogTitle sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", }} > Search Follow-Up <IconButton onClick={handleCloseSearchModal} sx={{ color: "grey.600" }}><HiOutlineXCircle /> </IconButton> </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}> <DateInput label="Inq. Date" name="inquiryDate" value={searchInquiryDate} onChange={handleSearchInquiryDateChange} fullWidth /> </Grid>
            <Grid item xs={6}> <FloatingLabelInput label="Inq. No" name="inquiryNumber" value={searchInquiryNumber} onChange={handleSearchInquiryNumberChange} fullWidth /> </Grid>
            <Grid item xs={12}> <FloatingLabelInput label="Client Name" name="companyName" value={searchCompanyName} onChange={handleSearchCompanyNameChange} fullWidth /> </Grid>
            <Grid item xs={12}> <FloatingLabelInput label="Contact Person" name="contactPerson" value={searchContactPerson} onChange={handleSearchContactPersonChange} fullWidth /> </Grid>
            <Grid item xs={12}> <FloatingLabelInput label="Mobile No" name="mobileNo" value={searchMobileNo} onChange={handleSearchMobileNoChange} fullWidth /> </Grid>
            <Grid item xs={6}> <Dropdown id="status-dropdown" label="Status" name="status" value={searchStatus} options={Array.isArray(statusOptions) ? statusOptions : []} valueKey="LeadStatus" labelKey="LeadStatus" onChange={(selectedOption) => setSearchStatus(selectedOption.LeadStatus)} fullWidth /> </Grid>
            <Grid item xs={6}> <Dropdown id="assign-dropdown" label="Assign" name="assign" value={searchAssign} options={Array.isArray(usersOptions) ? usersOptions : []} valueKey="employeeName" labelKey="employeeName" onChange={(selectedOption) => setSearchAssign(selectedOption.employeeName)} fullWidth /> </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {/* Search button triggers filtering using the current search values */}
          <Button variant="contained" color="primary" onClick={handleModalSearch}> Search </Button>
          {/* Reset button clears filter values and reloads initial records */}
          <Button variant="contained" color="secondary" onClick={handleCloseSearchModal}> Reset </Button>
        </DialogActions>
      </Dialog>

      {/* Render Inquiries */}
      {visibleTable && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, my: 2, px: { xs: 1, sm: 2, md: 3 }, mx: 1 }}>
          {(loggedInUser.userCategory === "Team Leader" ? [...(dataSets[visibleTable].self || []), ...(dataSets[visibleTable].team || [])] : dataSets[visibleTable]).map((record, idx) => {
            const color = avatarColors[idx % avatarColors.length];
            const priorityColor = getPriorityColor(record.priority);
            const pLetter = (record.priority || "").charAt(0).toUpperCase();

            return (
              <Paper key={record.inquiryId} elevation={2} onClick={() => handleRowClick(record)} sx={{ borderLeft: `6px solid ${priorityColor}`, borderRadius: 2, p: { xs: 2, md: 3 }, bgcolor: "background.paper", cursor: "pointer", "&:hover": { boxShadow: 4 }, position: "relative" }} >
                {/* History icon */}
                <IconButton size="small" onClick={e => { e.stopPropagation(); handleOpenHistory(record.inquiryId); }} sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,1)" } }} >
                  <HistoryIcon color="primary" fontSize="small" />
                </IconButton>

                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                  <Avatar sx={{ bgcolor: color, color: "#fff", width: 40, height: 40, mr: 2, }} > {record.clientname?.[0]?.toUpperCase() || "?"} </Avatar>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={400} noWrap
                      sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "100%",
                        fontSize: {
                          xs: "0.9rem",  // mobile
                          sm: "1rem",    // small screens
                          md: "1.05rem", // medium
                          lg: "1.1rem",  // large
                        },
                      }}
                    >
                      {record.clientname}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" noWrap > {record.city} </Typography>
                  </Box>

                  <Chip label={`${record.statusName} | ${pLetter}`} size="small" sx={{ bgcolor: priorityColor, color: "#fff", ml: 1, opacity: 0.7, }} />
                </Box>

                {/* Contact & Source */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <PersonIcon sx={{ fontSize: 16, mr: 1, color: "grey.600" }} />
                    <Typography variant="body2" noWrap> {record.contactPerson || "—"} </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <LinkIcon sx={{ fontSize: 16, mr: 1, color: "grey.600" }} />
                    <Typography variant="body2" noWrap> {record.LastleadSourceName || "—"} </Typography>
                  </Box>
                </Box>

                {/* Mobile & Price */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <PhoneIcon sx={{ fontSize: 16, mr: 1, color: "grey.600" }} />
                    <Typography variant="body2" color="primary" fontWeight={600} noWrap> {record.mobileNo || "—"} </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography fontSize={16} fontWeight={600} color="grey.700" sx={{ mr: 0.5 }}> ₹ </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap> {record.closingPrice ?? "0.00"} </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Footer */}
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AssignmentIndIcon sx={{ fontSize: 16, mr: 1, color: "grey.600" }} />
                    <Typography variant="body2" noWrap> {record.employeeName?.toUpperCase() || "—"} </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: "grey.600" }} />
                    <Typography variant="body2" noWrap> {formatYMDToDMY(record.nextFollowUpDate)} </Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* EnquiryForm Modal for Follow-Up Mode */}
      <Dialog open={showEnquiryFormModal} onClose={() => setShowEnquiryFormModal(false)} fullWidth maxWidth="xl" PaperProps={{ sx: { width: '95%', maxWidth: 'none', } }} >
        <DialogTitle>
          <Button onClick={() => setShowEnquiryFormModal(false)} sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "red", color: "white", minWidth: 0, width: 32, height: 32, borderRadius: "50%", "&:hover": { backgroundColor: "darkred" }, }} >
            &times;
          </Button>
        </DialogTitle>

        <DialogContent>
          <EnquiryForm loggedInUser={loggedInUser} inquiryData={selectedRecord} onClose={() => setShowEnquiryFormModal(false)} />
        </DialogContent>
      </Dialog>

      {/* History Management Modal */}
      <HistoryManagement open={showHistoryModal} onClose={() => setShowHistoryModal(false)} inquiryId={historyInquiryId} maxWidth={isDesktop ? "xl" : "sm"} PaperProps={{ sx: { width: isDesktop ? "900px" : "100%" } }} />
    </Box>
  );
};

export default FollowUpPage;